import type { IconName, ViewGroup, ViewItem } from "@omnabase/ui";
import { CUSTOMER_SEGMENTS, type CustomersSidebar, type Tag } from "@omnabase/shared";
import { poolChoices } from "../../lib/sidebarEdits";
import type { ColumnArea } from "../inbox/CustomizeSidebar";

/** The customers sidebar round (2026-10-29, brief rule 2 "The screen" with "After the red team's review"; report findings 11 and 12): the
 *  Customers column drawn from the person's own layout (`me.customersSidebar`), and the words its Customize dialog uses. Pure.
 *  - Groups in their order with their names, hidden groups and entries left out, a folded group as its heading with the first entry's number,
 *    marked current while the open entry sits inside; the built-in "Tags" group is not drawn while it holds nothing (question 4).
 *  - Exactly one entry is lit (question 9): the tag's when a tag is open, else a segment other than All, else the chosen project's entry, else
 *    All customers; none on a customer's own page.
 *  - A tag or project entry shows only once the tags or projects are known; its number comes from the column's counts read (`tagCounts`, `projectCounts`; the queue-and-chat-column round). */
export type CustomersColumnOpen = { segment: string; tagId: string | null; projectId: string | null; onProfile: boolean };
export type CustomersColumnCounts = { segment: (id: string) => number | undefined; tags?: Record<string, number>; projects?: Record<string, number> };
export type CustomersColumnInput = {
  layout: CustomersSidebar; tags: readonly Tag[]; projects: readonly { id: string; name: string }[]; counts: CustomersColumnCounts; open: CustomersColumnOpen;
  href: { segment: (id: string) => string; tag: (id: string) => string; project: (id: string) => string }; onFold: (groupId: string) => void;
};
type Entry = { kind: string; id: string };
const keyOf = (i: Entry) => `${i.kind}:${i.id}`;
const shown = <T extends { hidden: boolean }>(xs: readonly T[]) => xs.filter((x) => !x.hidden);

/** the key of the one lit entry (`kind:id`), given the entries the column draws; null on a customer's page or when the open tag is not drawn */
export function activeKey(open: CustomersColumnOpen, drawn: ReadonlySet<string>): string | null {
  if (open.onProfile) return null;
  if (open.tagId) return `customerTag:${open.tagId}`;
  if (open.segment !== "all") return `segment:${open.segment}`;
  if (open.projectId && drawn.has(`project:${open.projectId}`)) return `project:${open.projectId}`;
  return "segment:all";
}

/** an entry as the column draws it, or null while what it names is unknown (a tag not loaded yet, a project no longer open) */
function entryOf(o: CustomersColumnInput, i: Entry): Omit<ViewItem, "active"> | null {
  if (i.kind === "segment") {
    const s = CUSTOMER_SEGMENTS.find((x) => x.id === i.id);
    return s ? { id: keyOf(i), label: s.label, icon: s.icon as IconName, href: o.href.segment(s.id), count: o.counts.segment(s.id) } : null;
  }
  if (i.kind === "customerTag") {
    const t = o.tags.find((x) => x.id === i.id && x.kind === "customer");
    return t ? { id: keyOf(i), label: t.name, icon: "tag", href: o.href.tag(t.id), count: o.counts.tags?.[t.id] } : null;
  }
  const p = o.projects.find((x) => x.id === i.id);
  return p ? { id: keyOf(i), label: p.name, icon: "package", href: o.href.project(p.id), count: o.counts.projects?.[p.id] } : null;
}

export function customersViewGroups(o: CustomersColumnInput): ViewGroup[] {
  const drawnGroups = shown(o.layout.groups).map((g) => ({ g, items: shown(g.items).flatMap((i) => { const e = entryOf(o, i); return e ? [e] : []; }) }))
    .filter(({ g, items }) => g.builtIn !== "tags" || items.length > 0);
  const active = activeKey(o.open, new Set(drawnGroups.flatMap(({ items }) => items.map((e) => e.id))));
  return drawnGroups.map(({ g, items }) => {
    const entries: ViewItem[] = items.map((e) => ({ ...e, active: e.id === active }));
    return { label: g.name, items: entries, fold: { folded: g.folded, onToggle: () => o.onFold(g.id), count: entries[0]?.count, current: entries.some((e) => e.active) } };
  });
}

/** the name of the open entry, for the crumb and the title: the tag's, a segment's other than All, the chosen project's when its entry is
 *  lit; else null (the list is "All customers") */
export function openEntryName(o: Pick<CustomersColumnInput, "tags" | "projects">, open: CustomersColumnOpen, lit: string | null): string | null {
  if (open.tagId) return o.tags.find((t) => t.id === open.tagId)?.name ?? null;
  if (open.segment !== "all") return CUSTOMER_SEGMENTS.find((s) => s.id === open.segment)?.label ?? null;
  if (lit?.startsWith("project:")) return o.projects.find((p) => `project:${p.id}` === lit)?.name ?? null;
  return null;
}

/** the Customize dialog's words and pool for the Customers column (settings copy, 30 words at most); projects only with two or more open ones
 *  (question 6) */
export function customersArea(tags: readonly Tag[], projects: readonly { id: string; name: string }[]): ColumnArea {
  const look = (i: Entry): { label: string; icon: IconName } => {
    if (i.kind === "segment") { const s = CUSTOMER_SEGMENTS.find((x) => x.id === i.id); return { label: s?.label ?? i.id, icon: (s?.icon ?? "users") as IconName }; }
    if (i.kind === "customerTag") return { label: tags.find((t) => t.id === i.id)?.name ?? "Unknown tag", icon: "tag" };
    return { label: projects.find((p) => p.id === i.id)?.name ?? "Unknown project", icon: "package" };
  };
  return {
    title: "Customize the Customers column", intro: "Arrange the Customers column for yourself: show, hide, sort, group and remove its segments, customer tags and projects.",
    search: "Search segments, tags and projects", look,
    pool: (layout, search) => poolChoices(layout, tags, search, { projects: projects.length > 1 ? projects : [] }),
    kindLabel: (kind) => (kind === "customerTag" ? "Customer tag" : kind === "project" ? "Project" : null),
  };
}
